import { useState, type KeyboardEvent } from 'react'

import { errorMessage, isApiError } from '@/api/client'
import {
  useCreateLookup,
  useLandLookup,
  useLookup,
  type CaseRecordOut,
  type LandedOut,
  type LookupCandidate,
} from '@/api/registers'
import type { Jurisdiction } from '@/api/types'
import { Badge, Button, Field, Input, Select, type BadgeTone } from '@/components/ui'

/**
 * "Look up a number" — the wizard's fourth way in.
 *
 * Paste the number printed on the letter (`UK00003580698`, `90/229,178`,
 * `IR 1 886 984`) and the register's own record comes back: biblio, status,
 * history, whatever documents the register publishes, and — for WIPO — the
 * provisional refusal itself as a real PDF.
 *
 * Three honesty rules, all server decisions this component only renders:
 *
 * - **An ambiguous number is a choice, not a guess.** `1886984` is a valid US
 *   registration, a valid WIPO international registration and a valid UK
 *   application; the candidates are shown with each one's reason and the
 *   person picks.
 * - **The gaps are shown in words.** "UKIPO publishes no examination reports"
 *   and "EUIPO lists documents it will not hand over" are answers, not
 *   failures, and hiding them would turn honest absences into silent zeros.
 * - **A Madrid IR lands at a designated office.** `Jurisdiction` has no `WO`
 *   on purpose — the response is drafted under the refusing office's law — so
 *   landing a WIPO lookup asks which office's refusal this matter answers.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  queued: 'warn',
  running: 'warn',
  done: 'ok',
  failed: 'block',
}

const DOC_TONE: Record<string, BadgeTone> = {
  refusal: 'block',
  opposition: 'warn',
  status: 'neutral',
  other: 'neutral',
  unknown: 'neutral',
}

/** The offices a matter can be drafted for — `Jurisdiction`, spelled out. */
const LANDABLE: readonly Jurisdiction[] = ['US', 'GB', 'EM', 'IN']

export interface LookupHandoff {
  landed: LandedOut
  record: CaseRecordOut
  office: Jurisdiction
}

export function LookupSource({ onLanded }: { onLanded: (handoff: LookupHandoff) => void }) {
  const create = useCreateLookup()
  const land = useLandLookup()

  const [number, setNumber] = useState('')
  const [candidates, setCandidates] = useState<LookupCandidate[]>([])
  const [note, setNote] = useState('')
  const [lookupId, setLookupId] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [landOffice, setLandOffice] = useState<Jurisdiction>('GB')
  //: Which stored refusal to read. Empty means "let the platform choose",
  //: which picks the one whose text reads as this office's — the default that
  //: is right nearly always. The control exists because a Madrid registration
  //: is refused by each designated office separately and WIPO files every one
  //: of those notifications against the same number, so which document answers
  //: *this* matter is a question with a wrong answer: landing IR 1898434 as an
  //: India matter once read EUIPO's, and the confirm step came up with EUTMR
  //: Article 7 grounds under the Trade Marks Act 1999.
  const [documentBlobId, setDocumentBlobId] = useState('')

  const lookup = useLookup(lookupId)
  const record = lookup.data?.status === 'done' ? lookup.data.record : null
  const needsOfficeChoice =
    record !== null && !LANDABLE.includes(lookup.data?.office as Jurisdiction)

  // Not a <form>: this panel renders inside the wizard's own read-step form,
  // and forms do not nest. Enter in the input submits by hand instead.
  async function submit(office?: string) {
    setRefusal(null)
    setCandidates([])
    if (!number.trim()) return
    try {
      const resolution = await create.mutateAsync({ number: number.trim(), office })
      if (!resolution.resolved) {
        setCandidates(resolution.candidates)
        setNote(resolution.note)
        return
      }
      setLookupId(resolution.lookup?.id ?? null)
    } catch (caught) {
      setRefusal(
        isApiError(caught) && caught.status === 422
          ? caught.message
          : errorMessage(caught),
      )
    }
  }

  //: Refusals this platform could actually read. A document the register only
  //: lists (EUIPO's access model) or a scan with no text layer is not a choice
  //: anyone can make, so neither reaches the picker.
  const readableRefusals = (record?.documents ?? []).filter(
    (doc) => doc.kind === 'refusal' && doc.obtainable && !doc.needs_ocr,
  )

  async function startMatter() {
    if (!lookupId || !lookup.data || !record) return
    const office = needsOfficeChoice ? landOffice : (lookup.data.office as Jurisdiction)
    try {
      const landed = await land.mutateAsync({
        lookupId,
        office: needsOfficeChoice ? landOffice : undefined,
        documentBlobId: documentBlobId || undefined,
      })
      onLanded({ landed, record, office })
    } catch (caught) {
      setRefusal(errorMessage(caught))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <Field
          label="Look up a number"
          hint="The number printed on the letter — UK00003580698, 90/229,178, IR 1 886 984. Read from the register itself, over its public pages."
        >
          {({ id }) => (
            <div className="flex gap-2">
              <Input
                id={id}
                mono
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void submit()
                  }
                }}
                placeholder="UK00003580698"
              />
              <Button
                type="button"
                variant="secondary"
                loading={create.isPending}
                onClick={() => void submit()}
              >
                Look up
              </Button>
            </div>
          )}
        </Field>
      </div>

      {refusal ? (
        <p role="alert" className="m-0 text-meta text-warn-fg">
          {refusal}
        </p>
      ) : null}

      {candidates.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          {/* The server refuses to guess between these, and so does this
              screen: each candidate carries its reason and the person picks. */}
          <p className="m-0 text-meta text-muted">{note}</p>
          {candidates.map((candidate) => (
            <button
              key={candidate.office}
              type="button"
              className="flex flex-col items-start gap-0.5 rounded px-2 py-1.5 text-left hover:bg-divider"
              onClick={() => void submit(candidate.office)}
            >
              <span className="text-body">
                {candidate.office_name} {candidate.kind} {candidate.number}
              </span>
              <span className="text-micro text-muted">{candidate.why}</span>
            </button>
          ))}
        </div>
      ) : null}

      {lookupId && lookup.data ? (
        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-meta">
              {lookup.data.office_name} {lookup.data.number}
            </span>
            <Badge tone={STATUS_TONE[lookup.data.status] ?? 'neutral'}>
              {lookup.data.status === 'queued' || lookup.data.status === 'running'
                ? 'reading the register…'
                : lookup.data.status}
            </Badge>
          </div>

          {lookup.data.status === 'failed' ? (
            <p role="alert" className="m-0 text-meta text-warn-fg">
              {lookup.data.error}
            </p>
          ) : null}

          {record && !record.found ? (
            <p className="m-0 text-meta text-muted">
              {lookup.data.office_name} has no case for this number.
              {record.gaps.length > 0 ? ` ${record.gaps[0]}` : ''}
            </p>
          ) : null}

          {record?.found ? (
            <>
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-meta">
                <dt className="text-muted">Mark</dt>
                <dd className="m-0">{record.mark || '(none recorded)'}</dd>
                <dt className="text-muted">Owner</dt>
                <dd className="m-0">{record.owner || '(not published)'}</dd>
                <dt className="text-muted">Status</dt>
                <dd className="m-0">
                  {record.status || 'unknown'}
                  {record.status_date ? ` (${record.status_date})` : ''}
                </dd>
                {record.classes.length > 0 ? (
                  <>
                    <dt className="text-muted">Classes</dt>
                    <dd className="m-0">{record.classes.join(', ')}</dd>
                  </>
                ) : null}
              </dl>

              {record.documents.length > 0 ? (
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {record.documents.map((doc, index) => (
                    <li key={index} className="flex items-center gap-2 text-meta">
                      <Badge tone={DOC_TONE[doc.kind] ?? 'neutral'}>{doc.kind}</Badge>
                      <span className="text-muted">{doc.date}</span>
                      <span className="truncate">{doc.description}</span>
                      {doc.needs_ocr ? <Badge tone="warn">scan — needs OCR</Badge> : null}
                      {!doc.obtainable ? (
                        <Badge tone="neutral">listed, not published</Badge>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}

              {/* The case history the register published. The USPTO's note says
                  "the prosecution history above dates every office action", and
                  until this was drawn there was no "above" — the one list the
                  note sends a reader to was in the stored record and nowhere on
                  the screen (TM-12). Closed by default: it is long, and the
                  documents above it are what a person acts on. */}
              {(record.events ?? []).length > 0 ? (
                <details className="text-meta">
                  <summary className="cursor-pointer text-muted">
                    {record.office === 'US' ? 'Prosecution history' : 'Case history'} (
                    {(record.events ?? []).length})
                  </summary>
                  <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
                    {(record.events ?? []).map((entry, index) => (
                      <li key={index} className="flex gap-2">
                        <span className="shrink-0 text-muted">{entry.date}</span>
                        <span>{entry.what}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}

              {readableRefusals.length > 1 ? (
                <Field
                  label="Which refusal should the draft answer?"
                  hint="This registration carries more than one. Left on the default, the platform reads the one whose own text names the office you chose above — and says which in the notes."
                >
                  {({ id }) => (
                    <Select
                      id={id}
                      value={documentBlobId}
                      onChange={(event) => setDocumentBlobId(event.target.value)}
                    >
                      <option value="">
                        Let the platform choose (the one matching the office)
                      </option>
                      {readableRefusals.map((doc) => (
                        <option key={doc.blob_id} value={doc.blob_id}>
                          {doc.date} — {doc.description}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}

              {/* Honest absences, in the register's own terms. Never hidden:
                  "we looked and there were none" and "this office publishes
                  none" are different facts, and this is where they differ. */}
              {record.gaps.map((gap) => (
                <p key={gap} className="m-0 text-micro text-muted">
                  {gap}
                </p>
              ))}

              <div className="flex flex-wrap items-end gap-2">
                {needsOfficeChoice ? (
                  <Field
                    label="Which office's refusal does this matter answer?"
                    hint="A Madrid registration is refused by a designated office, and the response is drafted under that office's law."
                  >
                    {({ id }) => (
                      <Select
                        id={id}
                        value={landOffice}
                        onChange={(event) => setLandOffice(event.target.value as Jurisdiction)}
                      >
                        {LANDABLE.map((office) => (
                          <option key={office} value={office}>
                            {office}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                ) : null}
                <Button
                  variant="primary"
                  onClick={() => void startMatter()}
                  loading={land.isPending}
                  loadingLabel="Starting…"
                >
                  Start the matter from this case
                </Button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
